import { Box, Button, Stack, Text } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link as RouterLink, Navigate } from 'react-router'
import { api, isApiError } from '~/api/client'
import { useJoinById, useMe, useSignInSettings } from '~/api/hooks'
import { ErasureLines } from '~/components/member/ErasureLines'
import { FreshSignIn } from '~/components/member/FreshSignIn'
import { PasswordField } from '~/components/member/PasswordField'
import { ErrorAlert, RetryError, useFriendlyError } from '~/components/member/feedback'
import { BackLink } from '~/components/member/FlowStepper'
import { FormStack, PageTitle } from '~/components/member/layout'
import { useT } from '~/i18n/use-t'
import { forgetErasedPerson } from '~/lib/session'
import { useErased, useLogout } from './MemberShell'
import { StepSkeleton } from '~/components/member/StepSkeleton'

/**
 * The erasure needs a sign-in of the last 10 minutes when no password is typed (passwordless P2): on a 403
 * `fresh_sign_in_required` the voter signs in again with the sign-in email here, or asks for an emailed link that
 * deletes the data (`memberEraseLinkRequest`, 1 hour).
 */
function EraseFresh({ email, onSignedIn }: { email: string | null; onSignedIn: () => void }) {
  const { t } = useT()
  const friendly = useFriendlyError()
  const settings = useSignInSettings()
  const logout = useLogout()
  const link = useMutation({ mutationFn: () => api('memberEraseLinkRequest') })
  return (
    <Stack gap={5} borderTop='1px solid' borderColor='border' pt={4}>
      <FreshSignIn
        email={email}
        mode={settings.data?.mode}
        intro={t('deleteData.fresh')}
        onSignedIn={onSignedIn}
        onSignOut={logout ? () => logout.mutate() : undefined}
        signingOut={logout?.isPending}
        formId='erase-code-form'
      />
      <Stack gap={3}>
        <Text>{t('deleteData.linkText')}</Text>
        {link.isSuccess ? (
          <Text role='status'>{t('deleteData.linkSent')}</Text>
        ) : (
          <Button
            variant='outline'
            alignSelf={{ base: 'stretch', sm: 'flex-start' }}
            loading={link.isPending}
            onClick={() => link.mutate()}
          >
            {t('deleteData.linkButton')}
          </Button>
        )}
        {link.isError && <ErrorAlert>{friendly(link.error)}</ErrorAlert>}
      </Stack>
    </Stack>
  )
}

/**
 * "Delete my data" (voter page, copy v8; passwordless P2): a voter with a password types it; a voter without one
 * deletes with this session when it is a recent sign-in, else signs in again by email or asks for an erasure link.
 * The lines about the company that did the check come from the provider's `erasureNotice` when the page can learn it;
 * the voter answer only names the provider.
 */
export default function DeleteMyData() {
  const { t } = useT()
  const friendly = useFriendlyError()
  const qc = useQueryClient()
  const me = useMe()
  const settings = useSignInSettings(Boolean(me.data?.member))
  const hasPassword = settings.data?.hasPassword === true
  // Signed in within the last 10 minutes (by email or password): the password may be left empty (M4).
  const fresh = settings.data?.freshSignInUntil != null
  const join = useJoinById(me.data?.providerId).data
  const providerName = join?.displayName
  const [password, setPassword] = useState('')
  const [empty, setEmpty] = useState(false)
  const [signedInAgain, setSignedInAgain] = useState(false)
  const { erased, setErased } = useErased()
  const erase = useMutation({
    // A typed password, or none: a voter with a password who signed in by email within 10 minutes may leave it empty
    // (gate review M4; the server answers 403 fresh_sign_in_required otherwise).
    mutationFn: () => api('memberErase', { body: hasPassword && password ? { password } : {} }),
    onSuccess: () => {
      // The member shell then shows this page alone (no member menu: the session is gone).
      setErased({ hadCheck: Boolean(providerName) })
      forgetErasedPerson(qc)
    },
  })
  const wrong = erase.isError && isApiError(erase.error, 'invalid_credentials')
  const needsFresh = erase.isError && isApiError(erase.error, 'fresh_sign_in_required')
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (erase.isPending) return
    if (hasPassword && !password && !fresh) return setEmpty(true)
    setEmpty(false)
    erase.mutate()
  }
  // After the erasure (mobile review): a clean confirmation with its own title, outside the member menu.
  if (erased)
    return (
      <Stack gap={6} maxW='3xl'>
        <PageTitle title={t('deleteData.doneTitle')} />
        <Text role='status'>{t(erased.hadCheck ? 'deleteData.done' : 'deleteData.doneNoCheck')}</Text>
        <Button asChild size='lg' alignSelf={{ base: 'stretch', sm: 'flex-start' }}>
          <RouterLink to='/join'>{t('deleteData.again')}</RouterLink>
        </Button>
      </Stack>
    )
  // No session (a visitor, an expired session, a reload after the erasure): the form would only end in an error.
  if (me.isError && isApiError(me.error) && me.error.status === 401) return <Navigate to='/login' replace />
  if (me.isPending) return <StepSkeleton />
  if (me.isError) return <RetryError error={me.error} onRetry={() => void me.refetch()} />
  if (me.data.member && settings.isPending) return <StepSkeleton />
  if (settings.isError) return <RetryError error={settings.error} onRetry={() => void settings.refetch()} />
  return (
    <Stack gap={6} maxW='3xl'>
      <Box>
        <BackLink to='/me' />
      </Box>
      <PageTitle title={t('deleteData.title')} />
      <FormStack gap={5} onSubmit={submit} noValidate>
        <Text>{t('deleteData.text')}</Text>
        <ErasureLines notice={join?.erasureNotice} provider={providerName} />
        {hasPassword && (
          <PasswordField
            label={t('deleteData.password')}
            value={password}
            onChange={(v) => {
              setPassword(v)
              setEmpty(false)
              if (!erase.isPending) erase.reset()
            }}
            autoComplete='current-password'
            error={empty ? t('deleteData.errPasswordEmpty') : wrong ? t('deleteData.errPassword') : null}
          />
        )}
        {erase.isError && !wrong && !needsFresh && <ErrorAlert>{friendly(erase.error)}</ErrorAlert>}
        {signedInAgain && erase.isIdle && <Text role='status'>{t('deleteData.signedInAgain')}</Text>}
        {/* A darker red than Chakra's solid, so the white label reaches 4.5:1. */}
        <Button
          type='submit'
          size='lg'
          colorPalette='red'
          bg='red.700'
          _hover={{ bg: 'red.800' }}
          color='white'
          alignSelf={{ base: 'stretch', sm: 'flex-start' }}
          loading={erase.isPending}
        >
          {t('deleteData.button')}
        </Button>
      </FormStack>
      {needsFresh && (
        <EraseFresh
          email={me.data.member?.email ?? null}
          onSignedIn={() => {
            erase.reset()
            setSignedInAgain(true)
          }}
        />
      )}
    </Stack>
  )
}
